import tooltipStyles from '@/components/Tooltip/Tooltip.module.css';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { FC, ReactElement, useRef, useState } from 'react';

type ButtonWithTooltipProps = {
  showTooltip: boolean;
  tooltipContent: string;
  button: ReactElement;
};

const ButtonWithTooltip: FC<ButtonWithTooltipProps> = ({ showTooltip, tooltipContent, button }) => {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);

  const {
    x,
    y,
    reference,
    floating,
    strategy,
    context,
    placement,
    middlewareData: { arrow: { x: arrowX, y: arrowY } = {} },
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  const staticSide = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  }[placement.split('-')[0]];

  if (!showTooltip) return button;

  return (
    <>
      <span ref={reference} {...getReferenceProps()} style={{ display: 'inline-block' }}>
        {button}
      </span>
      <FloatingPortal>
        {open && (
          <div
            ref={floating}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
            className={tooltipStyles.tooltipContainer}
            {...getFloatingProps()}
          >
            <div
              ref={arrowRef}
              className={tooltipStyles.tooltipArrow}
              style={{
                left: arrowX != null ? `${arrowX}px` : '',
                top: arrowY != null ? `${arrowY}px` : '',
                right: '',
                bottom: '',
                [staticSide!]: '-4px',
              }}
            />
            <div
              style={{
                fontSize: '2.25rem',
                textAlign: 'justify',
                lineHeight: '3rem',
                maxWidth: '100%',
                wordWrap: 'break-word',
              }}
            >
              {tooltipContent}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};

export default ButtonWithTooltip;
